import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Crown, Medal, Award, Sparkles, Flame } from "lucide-react";

const METRIC_LABEL = {
  accuracy: "By Accuracy",
  mastered: "By Mastered",
  activity: "By Activity",
  points: "By Points",
  streak: "By Streak",
};
const METRIC_HINT = {
  accuracy: "Top accuracy % among classmates (10+ answers required)",
  mastered: "Most questions mastered (Level 5)",
  activity: "Most questions answered",
  points: "Correct×10 + Smart Reviews×20 + Streak×5 + Badges×15",
  streak: "Top day-streaks (current). Switch between friends and course-wide.",
};

function RankIcon({ rank }) {
  if (rank === 1) return <Crown className="w-5 h-5 text-amber-500" strokeWidth={1.75} />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" strokeWidth={1.75} />;
  if (rank === 3) return <Award className="w-5 h-5 text-amber-700" strokeWidth={1.75} />;
  return <span className="text-sm font-semibold text-slate-500 w-5 text-center">{rank}</span>;
}

export default function StudentLeaderboard() {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("points");
  const [period, setPeriod] = useState("all");
  const [streakScope, setStreakScope] = useState("friends");
  const [streakData, setStreakData] = useState(null);

  useEffect(() => {
    if (metric === "streak") return; // handled by separate effect
    setData(null);
    api.get(`/student/leaderboard?metric=${metric}&period=${period}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [], course_name: null }));
  }, [metric, period]);

  useEffect(() => {
    if (metric !== "streak") return;
    setStreakData(null);
    api.get(`/student/streak-leaderboard?scope=${streakScope}`)
      .then((r) => setStreakData(r.data))
      .catch(() => setStreakData({ entries: [] }));
  }, [metric, streakScope]);

  const metricValue = (e) => {
    if (metric === "accuracy") return `${e.accuracy}%`;
    if (metric === "mastered") return `${e.mastered_count}`;
    if (metric === "points") return `${e.points ?? 0}`;
    return `${e.total_answered}`;
  };

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5" /> Leaderboard
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {data?.course_name || "Your Course"}
        </h1>
        <p className="text-slate-600 mt-1">{METRIC_HINT[metric]}</p>
      </div>

      <div className="flex flex-wrap items-center gap-4 justify-between">
        <Tabs value={metric} onValueChange={setMetric}>
          <TabsList>
            <TabsTrigger value="points" data-testid="metric-points">By Points</TabsTrigger>
            <TabsTrigger value="accuracy" data-testid="metric-accuracy">By Accuracy</TabsTrigger>
            <TabsTrigger value="mastered" data-testid="metric-mastered">By Mastered</TabsTrigger>
            <TabsTrigger value="activity" data-testid="metric-activity">By Activity</TabsTrigger>
            <TabsTrigger value="streak" data-testid="metric-streak">By Streak 🔥</TabsTrigger>
          </TabsList>
        </Tabs>
        {metric === "streak" ? (
          <Tabs value={streakScope} onValueChange={setStreakScope}>
            <TabsList>
              <TabsTrigger value="friends" data-testid="streak-scope-friends">Friends</TabsTrigger>
              <TabsTrigger value="course" data-testid="streak-scope-course">Course-wide</TabsTrigger>
            </TabsList>
          </Tabs>
        ) : (
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList>
              <TabsTrigger value="all" data-testid="period-all">All Time</TabsTrigger>
              <TabsTrigger value="month" data-testid="period-month">This Month</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {metric === "streak" ? (
        <StreakBoard streakData={streakData} scope={streakScope} />
      ) : !data ? (
        <div className="text-slate-500">Loading…</div>
      ) : data.entries.length === 0 ? (
        <Card className="p-10 text-center border-slate-200" data-testid="leaderboard-empty">
          <Sparkles className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h2 className="font-display font-bold text-lg">No rankings yet</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
            {metric === "accuracy"
              ? "Classmates need to answer at least 10 questions to qualify for accuracy rankings."
              : "Be the first to climb the board — keep practicing!"}
          </p>
        </Card>
      ) : (
        <>
          <Card className="p-0 overflow-hidden border-slate-200">
            <div className="divide-y divide-slate-100">
              {data.entries.map((e) => (
                <div
                  key={e.student_id}
                  className={`flex items-center gap-4 px-5 py-4 ${e.is_you ? "bg-amber-50" : ""}`}
                  data-testid={`leaderboard-row-${e.rank}`}
                >
                  <div className="w-8 grid place-items-center">
                    <RankIcon rank={e.rank} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {e.display_name}
                      {e.is_you && <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">You</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {e.total_answered} answered · {e.accuracy}% accuracy · {e.mastered_count} mastered
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-2xl font-bold text-[#1E3A8A]">{metricValue(e)}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{METRIC_LABEL[metric].replace("By ", "")}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {data.your_rank && data.your_rank > 10 && (
            <Card className="p-4 border-slate-200 bg-blue-50 text-sm" data-testid="your-rank-card">
              You're currently <span className="font-bold">#{data.your_rank}</span> out of {data.total_qualified} qualified classmates — keep going!
            </Card>
          )}
          {!data.your_rank && metric === "accuracy" && (
            <Card className="p-4 border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="not-qualified-card">
              Answer at least 10 questions to qualify for the accuracy leaderboard.
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function StreakBoard({ streakData, scope }) {
  if (!streakData) return <div className="text-slate-500" data-testid="streak-board-loading">Loading…</div>;
  const entries = streakData.entries || [];
  if (entries.length === 0) {
    return (
      <Card className="p-10 text-center border-slate-200" data-testid="streak-board-empty">
        <Flame className="w-10 h-10 mx-auto text-orange-500 mb-3" />
        <h2 className="font-display font-bold text-lg">
          {scope === "friends" ? "No friends with streaks yet" : "No streaks in your course yet"}
        </h2>
        <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
          {scope === "friends"
            ? "Add some friends to compare streaks. The longer you study daily, the higher you climb."
            : "Be the first to start a streak — answer at least one question today!"}
        </p>
      </Card>
    );
  }
  return (
    <Card className="p-0 overflow-hidden border-slate-200" data-testid="streak-board-card">
      <div className="divide-y divide-slate-100">
        {entries.map((e, idx) => {
          const rank = idx + 1;
          const fireCount = Math.min(3, Math.ceil(e.current_streak / 7));
          return (
            <div
              key={e.student_id}
              className={`flex items-center gap-4 px-5 py-4 ${e.is_self ? "bg-amber-50" : ""}`}
              data-testid={`streak-board-row-${rank}`}
            >
              <div className="w-8 grid place-items-center">
                <RankIcon rank={rank} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate flex items-center gap-2">
                  {e.display_handle}
                  {e.is_self && <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">You</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Best streak: {e.longest_streak} days
                </div>
              </div>
              <div className="text-right shrink-0 flex items-center gap-2">
                <div className="flex">
                  {Array.from({ length: fireCount }).map((_, i) => (
                    <Flame key={i} className="w-4 h-4 text-orange-500" />
                  ))}
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-orange-600 tabular-nums">{e.current_streak}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">day{e.current_streak === 1 ? "" : "s"}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
